import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Box, Headphones, User, Phone, Mail, Edit, LogOut, Calendar } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { bookingAPI, authAPI, wishlistAPI } from '../services/api';
import LoginSignupModal from '../components/modals/LoginSignupModal';
import { getStayInfoFromBooking } from '../utils/stayBookingDisplay';
import {
  canOpenStayTicket,
  getStayBookingBadge,
  isStayBookingDeclined
} from '../utils/stayBookingStatus';

/** Re-enable when vouchers are live */
const ACCOUNT_VOUCHERS_ENABLED = false;

const Account = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, refreshUser } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [allBookings, setAllBookings] = useState([]);
  const [showAllBookings, setShowAllBookings] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, hasMore: false });
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editData, setEditData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: ''
  });
  const [updating, setUpdating] = useState(false);
  const [wishlistItems, setWishlistItems] = useState([]);

  // Fetch user's bookings and refresh user data if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Refresh user data to ensure we have the latest profile information
      refreshUserData();
      fetchUserBookings();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const loadWishlist = async () => {
      try {
        const response = await wishlistAPI.getWishlist();
        setWishlistItems(response.items || []);
      } catch (error) {
        console.error('Failed to load wishlist:', error);
      }
    };

    loadWishlist();
  }, [isAuthenticated]);

  const refreshUserData = async () => {
    try {
      await refreshUser();
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  };

  const fetchUserBookings = async (limit = 50, offset = 0, showAll = false) => {
    try {
      setLoadingBookings(true);
      console.log('Fetching bookings with:', { limit, offset, showAll });

      // Debug call to check database state
      try {
        const debugResponse = await fetch('/api/bookings/debug', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('token')}`
          }
        });
        const debugData = await debugResponse.json();
        console.log('Debug data:', debugData);
      } catch (debugError) {
        console.log('Debug call failed:', debugError);
      }

      const response = await bookingAPI.getMyBookings(limit, offset);
      console.log('Bookings API response:', response);

      if (showAll) {
        setAllBookings(response.bookings || []);
        console.log('Set allBookings:', response.bookings?.length || 0);
      } else {
        setBookings(response.bookings || []);
        console.log('Set bookings:', response.bookings?.length || 0);
      }

      setPagination({
        total: response.pagination?.total || 0,
        hasMore: response.pagination?.hasMore || false
      });
      console.log('Set pagination:', {
        total: response.pagination?.total || 0,
        hasMore: response.pagination?.hasMore || false
      });
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setLoadingBookings(false);
    }
  };

  const handleViewAllBookings = () => {
    navigate('/all-bookings');
  };

  const handleCloseAllBookings = () => {
    setShowAllBookings(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleWishlistNavigate = (item) => {
    navigate(item.path);
  };

  const handleRemoveWishlistItem = async (item, event) => {
    event.stopPropagation();
    try {
      await wishlistAPI.removeFromWishlist(item.type, item.id);
      setWishlistItems((prev) => prev.filter((wishlistItem) => !(wishlistItem.type === item.type && wishlistItem.id === item.id)));
    } catch (error) {
      console.error('Failed to remove wishlist item:', error);
    }
  };

  const handleEditProfile = () => {
    // Split name into first and last name
    const nameParts = (user?.name || '').split(' ');
    setEditData({
      firstName: nameParts[0] || '',
      lastName: nameParts.slice(1).join(' ') || '',
      email: user?.email || '',
      mobile: user?.mobile || ''
    });
    setShowEditProfile(true);
  };

  const handleUpdateProfile = useCallback(async () => {
    try {
      setUpdating(true);
      // Combine first and last name for the API
      const profileData = {
        name: `${editData.firstName} ${editData.lastName}`.trim(),
        email: editData.email,
        mobile: editData.mobile
      };
      await authAPI.updateProfile(profileData);
      // Refresh user data instead of reloading the page
      await refreshUserData();
      setShowEditProfile(false);
    } catch (error) {
      console.error('Profile update failed:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setUpdating(false);
    }
  }, [editData, refreshUserData]);

  const handleInputChange = useCallback((field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleBookingClick = async (booking) => {
    // For confirmed bookings, navigate to booking confirmation to show ticket
    if (isStayBookingDeclined(booking)) {
      try {
        const bookingResponse = await bookingAPI.getBookingById(booking.bookingId);
        navigate('/booking-detail', {
          state: { bookingData: bookingResponse.booking || booking }
        });
      } catch {
        navigate('/booking-detail', {
          state: { bookingData: booking }
        });
      }
    } else if (canOpenStayTicket(booking)) {
      // Fetch full booking details and navigate to confirmation page
      try {
        const bookingResponse = await bookingAPI.getBookingById(booking.bookingId);
        if (bookingResponse.booking) {
          // Navigate with booking data - BookingConfirmation will handle the display
          navigate('/booking-confirmation', {
            state: {
              bookingData: bookingResponse.booking,
              fromAccount: true // Flag to indicate this is from account page
            }
          });
        } else {
          // Fallback: navigate with basic data
          navigate('/booking-confirmation', {
            state: {
              bookingId: booking.bookingId
            }
          });
        }
      } catch (error) {
        console.error('Failed to fetch booking details:', error);
        // Fallback: navigate with booking ID
        navigate('/booking-confirmation', {
          state: {
            bookingId: booking.bookingId
          }
        });
      }
    } else {
      // For pending/failed bookings, show booking details in a simple view
      // Or navigate to booking confirmation with status info
      navigate('/booking-confirmation', {
        state: {
          bookingId: booking.bookingId,
          bookingStatus: booking.status,
          paymentStatus: booking.paymentStatus
        }
      });
    }
  };

  // Edit Profile Modal
  const EditProfileModal = useMemo(() => {
    if (!showEditProfile) return null;

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 md:flex md:items-center md:justify-center md:p-6"
        onClick={() => setShowEditProfile(false)}
        role="presentation"
      >
        <div
          className="bg-[#FAF7F2] w-full h-full flex flex-col min-h-0 md:h-auto md:max-h-[min(90vh,720px)] md:w-full md:max-w-[480px] md:rounded-[24px] md:shadow-[0_20px_50px_rgba(0,0,0,0.15)] md:border md:border-[#E5E3DF] md:overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-profile-title"
        >
          {/* Header */}
          <div className="flex items-center gap-[16px] p-[16px] shrink-0 border-b border-transparent md:border-[#E5E3DF]">
            <button
              type="button"
              onClick={() => setShowEditProfile(false)}
              className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-black/5 transition-colors"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <g clip-path="url(#clip0_3263_125)">
                  <path d="M5 12H19" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M5 12L11 18" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M5 12L11 6" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                </g>
                <defs>
                  <clipPath id="clip0_3263_125">
                    <rect width="24" height="24" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </button>
            <h1 id="edit-profile-title" className="text-[20px] md:text-[24px] font-medium tracking-[-1px] text-black">
              My Profile
            </h1>
          </div>

          {/* Form Fields */}
          <div className="space-y-[20px] p-[20px] flex-1 overflow-y-auto min-h-0">
            {/* First Name */}
            <div>
              <label className="block text-[14px] md:text-[16px] leading-tight text-[#9d9d9d] mb-[8px]">First Name</label>
              <div className="relative">
                <input
                  type="text"
                  value={editData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className="w-full p-[16px] bg-[#FAF7F2] border border-[#DDD6CB] rounded-[12px] text-[16px] md:text-[17px] text-black focus:outline-none focus:border-[#FF5E4C]"
                  placeholder="Enter first name"
                />
                <button className="absolute right-[16px] top-1/2 transform -translate-y-1/2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <g clip-path="url(#clip0_3263_135)">
                      <path d="M3.33334 16.6668H6.66668L15.4167 7.91676C15.6355 7.69789 15.8092 7.43805 15.9276 7.15208C16.0461 6.86612 16.107 6.55962 16.107 6.25009C16.107 5.94056 16.0461 5.63406 15.9276 5.3481C15.8092 5.06213 15.6355 4.80229 15.4167 4.58342C15.1978 4.36455 14.938 4.19094 14.652 4.07248C14.366 3.95403 14.0595 3.89307 13.75 3.89307C13.4405 3.89307 13.134 3.95403 12.848 4.07248C12.562 4.19094 12.3022 4.36455 12.0833 4.58342L3.33334 13.3334V16.6668Z" stroke="#307FE2" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M11.25 5.41663L14.5833 8.74996" stroke="#307FE2" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                    </g>
                    <defs>
                      <clipPath id="clip0_3263_135">
                        <rect width="20" height="20" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </button>
              </div>
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-[14px] md:text-[16px] leading-tight text-[#9d9d9d] mb-[8px]">Last Name</label>
              <div className="relative">
                <input
                  type="text"
                  value={editData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className="w-full p-[16px] bg-[#FAF7F2] border border-[#DDD6CB] rounded-[12px] text-[16px] md:text-[17px] text-black focus:outline-none focus:border-[#FF5E4C]"
                  placeholder="Enter last name"
                />
                <button className="absolute right-[16px] top-1/2 transform -translate-y-1/2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <g clip-path="url(#clip0_3263_135)">
                      <path d="M3.33334 16.6668H6.66668L15.4167 7.91676C15.6355 7.69789 15.8092 7.43805 15.9276 7.15208C16.0461 6.86612 16.107 6.55962 16.107 6.25009C16.107 5.94056 16.0461 5.63406 15.9276 5.3481C15.8092 5.06213 15.6355 4.80229 15.4167 4.58342C15.1978 4.36455 14.938 4.19094 14.652 4.07248C14.366 3.95403 14.0595 3.89307 13.75 3.89307C13.4405 3.89307 13.134 3.95403 12.848 4.07248C12.562 4.19094 12.3022 4.36455 12.0833 4.58342L3.33334 13.3334V16.6668Z" stroke="#307FE2" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M11.25 5.41663L14.5833 8.74996" stroke="#307FE2" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                    </g>
                    <defs>
                      <clipPath id="clip0_3263_135">
                        <rect width="20" height="20" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </button>
              </div>
            </div>

            {/* Mobile Number */}
            <div>
              <label className="block text-[14px] md:text-[16px] leading-tight text-[#9d9d9d] mb-[8px]">Mobile Number</label>
              <div className="relative">
                <input
                  type="tel"
                  value={editData.mobile}
                  onChange={(e) => handleInputChange('mobile', e.target.value)}
                  className="w-full p-[16px] bg-[#FAF7F2] border border-[#DDD6CB] rounded-[12px] text-[16px] md:text-[17px] text-black focus:outline-none focus:border-[#FF5E4C]"
                  placeholder="Enter mobile number"
                />
                <button className="absolute right-[16px] top-1/2 transform -translate-y-1/2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <g clip-path="url(#clip0_3263_135)">
                      <path d="M3.33334 16.6668H6.66668L15.4167 7.91676C15.6355 7.69789 15.8092 7.43805 15.9276 7.15208C16.0461 6.86612 16.107 6.55962 16.107 6.25009C16.107 5.94056 16.0461 5.63406 15.9276 5.3481C15.8092 5.06213 15.6355 4.80229 15.4167 4.58342C15.1978 4.36455 14.938 4.19094 14.652 4.07248C14.366 3.95403 14.0595 3.89307 13.75 3.89307C13.4405 3.89307 13.134 3.95403 12.848 4.07248C12.562 4.19094 12.3022 4.36455 12.0833 4.58342L3.33334 13.3334V16.6668Z" stroke="#307FE2" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M11.25 5.41663L14.5833 8.74996" stroke="#307FE2" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                    </g>
                    <defs>
                      <clipPath id="clip0_3263_135">
                        <rect width="20" height="20" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </button>
              </div>
            </div>

            {/* Email Address */}
            <div>
              <label className="block text-[14px] md:text-[16px] leading-tight text-[#9d9d9d] mb-[8px]">Email Address</label>
              <div className="relative">
                <input
                  type="email"
                  value={editData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full p-[16px] bg-[#FAF7F2] border border-[#DDD6CB] rounded-[12px] text-[16px] md:text-[17px] text-black focus:outline-none focus:border-[#FF5E4C]"
                  placeholder="Enter email address"
                />
                <button className="absolute right-[16px] top-1/2 transform -translate-y-1/2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <g clip-path="url(#clip0_3263_135)">
                      <path d="M3.33334 16.6668H6.66668L15.4167 7.91676C15.6355 7.69789 15.8092 7.43805 15.9276 7.15208C16.0461 6.86612 16.107 6.55962 16.107 6.25009C16.107 5.94056 16.0461 5.63406 15.9276 5.3481C15.8092 5.06213 15.6355 4.80229 15.4167 4.58342C15.1978 4.36455 14.938 4.19094 14.652 4.07248C14.366 3.95403 14.0595 3.89307 13.75 3.89307C13.4405 3.89307 13.134 3.95403 12.848 4.07248C12.562 4.19094 12.3022 4.36455 12.0833 4.58342L3.33334 13.3334V16.6668Z" stroke="#307FE2" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M11.25 5.41663L14.5833 8.74996" stroke="#307FE2" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                    </g>
                    <defs>
                      <clipPath id="clip0_3263_135">
                        <rect width="20" height="20" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </button>
              </div>
            </div>
          </div>
          {/* Save Button */}
          <div className="shrink-0 w-full p-[20px] border-t border-[#dddddd] bg-[#FAF7F2] md:rounded-b-[24px]">
            <button
              type="button"
              onClick={handleUpdateProfile}
              disabled={updating || !editData.firstName.trim()}
              className={`w-full py-[16px] rounded-[14px] font-bold text-[16px] md:text-[17px] transition-colors relative overflow-hidden ${updating || !editData.firstName.trim()
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-[#131313] text-white hover:bg-gray-800'
                }`}
            >
              {updating ? 'Saving...' : 'Save Changes'}
              <svg className='absolute right-[10%] bottom-[-5%]' xmlns="http://www.w3.org/2000/svg" width="61" height="36" viewBox="0 0 61 36" fill="none">
                <path d="M17.8291 8.93766C17.4553 8.93766 17.0741 8.84601 16.7368 8.76537C15.8388 8.5491 13.7384 8.04692 14.2406 5.94657C14.4348 5.13283 14.8784 4.42354 15.4887 3.94885C16.1375 3.44301 16.9274 3.25424 17.6623 3.43019C19.1322 3.78208 19.99 5.48471 19.5739 7.22401C19.244 8.60224 18.5476 8.93766 17.8291 8.93766ZM17.1107 4.83226C16.7955 4.83226 16.5315 4.99538 16.3886 5.10717C16.0458 5.37476 15.7819 5.80545 15.6665 6.2893C15.5602 6.731 15.5748 6.98025 17.0777 7.33947C17.3123 7.39629 17.8053 7.51176 17.9171 7.46594C17.9171 7.46594 18.0289 7.37613 18.1462 6.88311C18.3698 5.94657 17.9923 5.0192 17.3196 4.85792C17.2482 4.84143 17.1785 4.83226 17.1107 4.83226Z" fill="#808080" />
                <path d="M9.68653 8.10003C9.35114 8.10003 9.01389 8.05238 8.70599 7.90026C8.1305 7.61435 7.79693 7.04986 7.68879 6.17929C7.58982 5.38204 7.74927 4.59395 8.13781 3.95982C8.55385 3.28352 9.20448 2.84365 9.92659 2.75568C10.6432 2.66587 11.3855 2.93164 11.9555 3.48514C12.4888 4.00381 12.8352 4.72956 12.9342 5.52682C13.0716 6.62648 12.5694 7.21665 12.1222 7.51722C11.6182 7.85811 10.9695 7.97539 10.3995 8.04687C10.1704 8.07619 9.92846 8.10003 9.68287 8.10003H9.68653ZM10.2088 4.20173C10.1759 4.20173 10.1429 4.20357 10.108 4.20907C9.72865 4.25672 9.49224 4.55362 9.38777 4.7259C9.17517 5.07229 9.08718 5.53416 9.144 5.99602C9.18432 6.31858 9.26499 6.53851 9.35846 6.58616C9.53807 6.67597 10.0347 6.61366 10.2217 6.58983C10.5956 6.54401 11.0556 6.46888 11.303 6.30026C11.3891 6.24161 11.5358 6.14264 11.4808 5.70461C11.424 5.24275 11.2242 4.81388 10.9328 4.53164C10.8008 4.40334 10.5406 4.1999 10.2088 4.1999V4.20173Z" fill="#808080" />
                <path d="M4.47307 12.2714C3.619 12.2714 3.12602 11.7308 2.90609 11.4045C2.08684 10.1839 2.35989 8.55825 3.51637 7.78115C4.67284 7.00589 6.28202 7.36512 7.1031 8.58574C7.52281 9.21071 7.6291 9.81002 7.422 10.369C7.21123 10.939 6.72188 11.3221 6.31867 11.5933C5.57274 12.0937 4.96425 12.2714 4.47307 12.2714ZM4.87631 8.83499C4.6857 8.83499 4.49689 8.88815 4.33377 8.99628C3.84809 9.32251 3.75281 10.0355 4.12302 10.5853C4.22199 10.7337 4.45659 11.0765 5.50127 10.3745C5.91181 10.0996 6.01994 9.93281 6.04743 9.85767C6.08958 9.74221 5.9833 9.5461 5.88617 9.40131C5.64058 9.03659 5.25203 8.83316 4.87631 8.83316V8.83499Z" fill="#808080" />
                <path d="M22.6033 13.0027C21.9069 13.0027 21.2874 12.5867 20.8989 12.3264C20.2739 11.9049 18.3678 10.6256 19.6837 8.66823C20.7192 7.12687 22.6198 6.58254 23.9193 7.45677C25.2187 8.331 25.435 10.2939 24.3995 11.8353C23.8056 12.7205 23.1788 13.0027 22.6033 13.0027ZM22.5117 8.50146C21.9545 8.50146 21.3149 8.86618 20.9007 9.48382C20.591 9.94568 20.492 10.2847 21.7163 11.1077C22.6235 11.7161 22.7463 11.6666 23.1825 11.016C23.758 10.1601 23.7195 9.0861 23.1018 8.6719C22.9296 8.55643 22.7261 8.50146 22.5117 8.50146Z" fill="#808080" />
                <path d="M22.7727 42.5084H4.44509C4.24348 42.5084 4.05106 42.4259 3.91177 42.2793C3.77431 42.1327 3.70098 41.9348 3.71198 41.735C3.71931 41.603 4.44325 28.4933 4.44325 22.7146C4.44325 18.8547 3.80547 16.3274 3.36744 15.0444C2.50604 14.5423 1.74727 13.822 1.16079 12.9496C-0.642656 10.2646 -0.307292 6.8703 1.90669 5.3821C2.92021 4.70214 4.20499 4.51885 5.48976 4.84325C5.75001 2.29754 7.38668 0.307158 9.58417 0.0322431C11.2776 -0.180358 12.9088 0.670053 13.9828 2.2389C15.1979 1.03661 16.7686 0.521594 18.2715 0.882649C20.0181 1.30235 21.3157 2.84371 21.6914 4.85059C22.9909 4.5042 24.2701 4.68748 25.3075 5.38393C27.5215 6.87214 27.8568 10.2664 26.0534 12.9514C25.4669 13.8256 24.7082 14.5441 23.8468 15.0463C23.4069 16.3274 22.7691 18.8529 22.7691 22.7164C22.7691 28.4933 23.493 41.6049 23.5003 41.7368C23.5113 41.9384 23.4398 42.1345 23.3006 42.2812C23.1613 42.4278 22.9688 42.5103 22.7672 42.5103L22.7727 42.5084ZM5.21851 41.0422H21.9975C21.8417 38.1244 21.3047 27.7125 21.3047 22.7146C21.3047 18.3507 22.0928 15.5594 22.562 14.2838C22.6243 14.1134 22.7489 13.9723 22.912 13.888C23.6635 13.4958 24.3288 12.8891 24.8383 12.1322C26.1909 10.1198 26.035 7.6364 24.4919 6.60089C23.6415 6.0309 22.5052 5.99973 21.3725 6.5184C21.1508 6.62104 20.8923 6.60455 20.6816 6.47625C20.4708 6.34796 20.3425 6.12253 20.3333 5.87877C20.2673 4.07166 19.3015 2.63661 17.9288 2.30855C16.7063 2.0153 15.3757 2.63845 14.4611 3.93788C14.3108 4.15048 14.0616 4.2696 13.794 4.24577C13.5319 4.22194 13.3047 4.05884 13.1947 3.82058C12.4671 2.23524 11.1237 1.32069 9.76378 1.48747C8.0648 1.70007 6.81669 3.60981 6.92482 5.83845C6.93765 6.09504 6.81302 6.3388 6.60042 6.48359C6.38599 6.62655 6.11109 6.64669 5.88016 6.53673C4.73651 5.99973 3.58737 6.02174 2.72231 6.60089C1.17912 7.63824 1.02331 10.1198 2.37589 12.1322C2.8854 12.8891 3.55069 13.4976 4.30213 13.888C4.46341 13.9723 4.58802 14.1134 4.65217 14.2857C5.12136 15.5613 5.90947 18.3544 5.90947 22.7146C5.90947 27.7125 5.37246 38.1244 5.21667 41.0422H5.21851Z" fill="#808080" />
                <path d="M16.1762 19.7822C15.3551 19.7822 14.9995 19.5824 14.6495 19.3405C14.4314 19.1902 14.2261 19.0491 13.2438 19.0491C12.2614 19.0491 12.0561 19.1902 11.838 19.3405C11.488 19.5824 11.1324 19.7822 10.3113 19.7822C9.29598 19.7822 7.37891 18.8621 7.37891 15.3835C7.37891 12.134 10.5881 9.51868 13.2438 9.51868C15.8994 9.51868 19.1086 12.134 19.1086 15.3835C19.1086 18.8621 17.1915 19.7822 16.1762 19.7822ZM13.2438 17.5828C14.5743 17.5828 15.0454 17.8321 15.4816 18.1345C15.6612 18.2591 15.7436 18.316 16.1762 18.316C16.5849 18.316 17.6424 17.7881 17.6424 15.3835C17.6424 12.8323 15.0142 10.9849 13.2438 10.9849C11.4733 10.9849 8.84512 12.8323 8.84512 15.3835C8.84512 17.7881 9.90262 18.316 10.3113 18.316C10.742 18.316 10.8245 18.2591 11.006 18.1345C11.444 17.8339 11.9132 17.5828 13.2438 17.5828Z" fill="#808080" />
                <path d="M42.3818 8.93766C42.7557 8.93766 43.1369 8.84601 43.4741 8.76537C44.3722 8.5491 46.4725 8.04692 45.9704 5.94657C45.7761 5.13283 45.3325 4.42354 44.7222 3.94885C44.0734 3.44301 43.2835 3.25424 42.5486 3.43019C41.0787 3.78208 40.221 5.48471 40.637 7.22401C40.9669 8.60224 41.6633 8.93766 42.3818 8.93766ZM43.1002 4.83226C43.4155 4.83226 43.6794 4.99538 43.8224 5.10717C44.1651 5.37476 44.429 5.80545 44.5445 6.2893C44.6508 6.731 44.6361 6.98025 43.1332 7.33947C42.8986 7.39629 42.4056 7.51176 42.2938 7.46594C42.2938 7.46594 42.182 7.37613 42.0647 6.88311C41.8411 5.94657 42.2187 5.0192 42.8913 4.85792C42.9628 4.84143 43.0324 4.83226 43.1002 4.83226Z" fill="#808080" />
                <path d="M50.5283 8.10003C50.8637 8.10003 51.201 8.05238 51.5089 7.90026C52.0843 7.61435 52.4179 7.04986 52.526 6.17929C52.625 5.38204 52.4656 4.59395 52.077 3.95982C51.661 3.28352 51.0104 2.84365 50.2883 2.75568C49.5716 2.66587 48.8294 2.93164 48.2594 3.48514C47.726 4.00381 47.3796 4.72956 47.2807 5.52682C47.1432 6.62648 47.6454 7.21665 48.0926 7.51722C48.5966 7.85811 49.2454 7.97539 49.8154 8.04687C50.0445 8.07619 50.2864 8.10003 50.532 8.10003H50.5283ZM50.006 4.20173C50.039 4.20173 50.072 4.20357 50.1068 4.20907C50.4862 4.25672 50.7226 4.55362 50.8271 4.7259C51.0397 5.07229 51.1277 5.53416 51.0708 5.99602C51.0305 6.31858 50.9499 6.53851 50.8564 6.58616C50.6768 6.67597 50.1801 6.61366 49.9932 6.58983C49.6193 6.54401 49.1593 6.46888 48.9118 6.30026C48.8257 6.24161 48.6791 6.14264 48.7341 5.70461C48.7909 5.24275 48.9907 4.81388 49.2821 4.53164C49.414 4.40334 49.6743 4.1999 50.006 4.1999V4.20173Z" fill="#808080" />
                <path d="M55.7418 12.2714C56.5958 12.2714 57.0888 11.7308 57.3088 11.4045C58.128 10.1839 57.855 8.55825 56.6985 7.78115C55.542 7.00589 53.9328 7.36512 53.1117 8.58574C52.692 9.21071 52.5857 9.81002 52.7928 10.369C53.0036 10.939 53.493 11.3221 53.8962 11.5933C54.6421 12.0937 55.2506 12.2714 55.7418 12.2714ZM55.3385 8.83499C55.5291 8.83499 55.718 8.88815 55.8811 8.99628C56.3668 9.32251 56.462 10.0355 56.0918 10.5853C55.9929 10.7337 55.7583 11.0765 54.7136 10.3745C54.303 10.0996 54.1949 9.93281 54.1674 9.85767C54.1253 9.74221 54.2315 9.5461 54.3287 9.40131C54.5743 9.03659 54.9628 8.83316 55.3385 8.83316V8.83499Z" fill="#808080" />
                <path d="M37.6115 13.0027C38.308 13.0027 38.9274 12.5867 39.316 12.3264C39.941 11.9049 41.8471 10.6256 40.5311 8.66823C39.4956 7.12687 37.595 6.58254 36.2956 7.45677C34.9962 8.331 34.7799 10.2939 35.8154 11.8353C36.4092 12.7205 37.036 13.0027 37.6115 13.0027ZM37.7031 8.50146C38.2603 8.50146 38.8999 8.86618 39.3142 9.48382C39.6239 9.94568 39.7229 10.2847 38.4986 11.1077C37.5914 11.7161 37.4686 11.6666 37.0324 11.016C36.4569 10.1601 36.4954 9.0861 37.113 8.6719C37.2853 8.55643 37.4887 8.50146 37.7031 8.50146Z" fill="#808080" />
                <path d="M37.4421 42.5084H55.7698C55.9714 42.5084 56.1638 42.4259 56.3031 42.2793C56.4405 42.1327 56.5139 41.9348 56.5029 41.735C56.4955 41.603 55.7716 28.4933 55.7716 22.7146C55.7716 18.8547 56.4094 16.3274 56.8474 15.0444C57.7088 14.5423 58.4676 13.822 59.0541 12.9496C60.8575 10.2646 60.5221 6.8703 58.3082 5.3821C57.2946 4.70214 56.0099 4.51885 54.7251 4.84325C54.4648 2.29754 52.8282 0.307158 50.6307 0.0322431C48.9372 -0.180358 47.306 0.670053 46.232 2.2389C45.0169 1.03661 43.4463 0.521594 41.9434 0.882649C40.1968 1.30235 38.8991 2.84371 38.5234 4.85059C37.224 4.5042 35.9447 4.68748 34.9074 5.38393C32.6934 6.87214 32.358 10.2664 34.1615 12.9514C34.748 13.8256 35.5067 14.5441 36.3681 15.0463C36.8079 16.3274 37.4458 18.8529 37.4458 22.7164C37.4458 28.4933 36.7218 41.6049 36.7145 41.7368C36.7035 41.9384 36.775 42.1345 36.9143 42.2812C37.0536 42.4278 37.246 42.5103 37.4476 42.5103L37.4421 42.5084ZM54.9963 41.0422H38.2174C38.3731 38.1244 38.9101 27.7125 38.9101 22.7146C38.9101 18.3507 38.122 15.5594 37.6529 14.2838C37.5905 14.1134 37.4659 13.9723 37.3028 13.888C36.5514 13.4958 35.8861 12.8891 35.3766 12.1322C34.024 10.1198 34.1798 7.6364 35.723 6.60089C36.5734 6.0309 37.7097 5.99973 38.8423 6.5184C39.0641 6.62104 39.3225 6.60455 39.5333 6.47625C39.744 6.34796 39.8724 6.12253 39.8815 5.87877C39.9475 4.07166 40.9133 2.63661 42.2861 2.30855C43.5085 2.0153 44.8392 2.63845 45.7537 3.93788C45.904 4.15048 46.1532 4.2696 46.4208 4.24577C46.6829 4.22194 46.9102 4.05884 47.0201 3.82058C47.7477 2.23524 49.0912 1.32069 50.4511 1.48747C52.15 1.70007 53.3982 3.60981 53.29 5.83845C53.2772 6.09504 53.4018 6.3388 53.6144 6.48359C53.8289 6.62655 54.1038 6.64669 54.3347 6.53673C55.4783 5.99973 56.6275 6.02174 57.4925 6.60089C59.0357 7.63824 59.1915 10.1198 57.839 12.1322C57.3294 12.8891 56.6642 13.4976 55.9127 13.888C55.7514 13.9723 55.6268 14.1134 55.5627 14.2857C55.0935 15.5613 54.3054 18.3544 54.3054 22.7146C54.3054 27.7125 54.8424 38.1244 54.9982 41.0422H54.9963Z" fill="#808080" />
                <path d="M44.0387 19.7822C44.8597 19.7822 45.2153 19.5824 45.5654 19.3405C45.7835 19.1902 45.9887 19.0491 46.9711 19.0491C47.9534 19.0491 48.1587 19.1902 48.3768 19.3405C48.7269 19.5824 49.0824 19.7822 49.9035 19.7822C50.9189 19.7822 52.8359 18.8621 52.8359 15.3835C52.8359 12.134 49.6268 9.51868 46.9711 9.51868C44.3154 9.51868 41.1062 12.134 41.1062 15.3835C41.1062 18.8621 43.0233 19.7822 44.0387 19.7822ZM46.9711 17.5828C45.6405 17.5828 45.1695 17.8321 44.7333 18.1345C44.5537 18.2591 44.4712 18.316 44.0387 18.316C43.63 18.316 42.5724 17.7881 42.5724 15.3835C42.5724 12.8323 45.2006 10.9849 46.9711 10.9849C48.7415 10.9849 51.3697 12.8323 51.3697 15.3835C51.3697 17.7881 50.3122 18.316 49.9035 18.316C49.4728 18.316 49.3903 18.2591 49.2089 18.1345C48.7709 17.8339 48.3017 17.5828 46.9711 17.5828Z" fill="#808080" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }, [showEditProfile, editData, handleInputChange, handleUpdateProfile, updating]);

  // Show login modal if not authenticated
  if (!isAuthenticated) {
    return (
      <>
        <div className="min-h-screen bg-[#FAF7F2] md:px-[120px] md:py-[60px]">
          {/* Header */}
          <div className=" px-[20px] py-[16px] flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center justify-center w-8 h-8"
            >
              <ArrowLeft size={20} className="text-black" />
            </button>
            <h1 className="text-[20px] md:text-[24px] font-medium text-black tracking-[-1px]">Account</h1>
            <div className="w-8"></div>
          </div>

          {/* Debug Info */}
          {process.env.NODE_ENV === 'development' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-[12px] p-[16px] m-[16px]">
              <div className="text-[12px] font-medium text-yellow-800 mb-2">Debug Info:</div>
              <div className="text-[11px] text-yellow-700 space-y-1">
                <div>Authenticated: {isAuthenticated ? 'Yes' : 'No'}</div>
                <div>User: {user?.name || 'Not logged in'}</div>
                <div>Bookings loaded: {bookings.length}</div>
                <div>All bookings loaded: {allBookings.length}</div>
                <div>Pagination total: {pagination.total}</div>
                <div>Show all bookings: {showAllBookings ? 'Yes' : 'No'}</div>
                <div className="flex space-x-2">
                  <button
                    onClick={async () => {
                      try {
                        const response = await fetch('/api/bookings/test-booking', {
                          method: 'POST',
                          headers: {
                            Authorization: `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('token')}`,
                            'Content-Type': 'application/json'
                          }
                        });
                        const data = await response.json();
                        console.log('Test booking result:', data);
                        if (response.ok) {
                          alert('Test booking created! Refreshing bookings...');
                          fetchUserBookings();
                        } else {
                          alert('Failed to create test booking: ' + data.error);
                        }
                      } catch (error) {
                        console.error('Test booking error:', error);
                        alert('Error creating test booking');
                      }
                    }}
                    className="mt-2 px-2 py-1 bg-blue-500 text-white text-[10px] rounded"
                  >
                    Create Test Booking
                  </button>

                  <button
                    onClick={async () => {
                      try {
                        const response = await fetch('/api/bookings/debug', {
                          headers: {
                            Authorization: `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('token')}`
                          }
                        });
                        const data = await response.json();
                        console.log('Debug response:', data);
                        alert(`Debug Info:\nUser Items: ${data.allUserItems}\nEdge Items: ${data.edgeItems}\nAll Bookings: ${data.allBookings}`);
                      } catch (error) {
                        console.error('Debug error:', error);
                        alert('Error getting debug info');
                      }
                    }}
                    className="mt-2 px-2 py-1 bg-green-500 text-white text-[10px] rounded"
                  >
                    Debug Info
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Login Prompt */}
          <div className="md:p-[20px] w-full flex flex-col md:flex-row items-center gap-[20px] md:gap-[120px]">
            <div className="text-center flex flex-col md:w-1/3 items-center p-[20px]">
              <div className="md:w-[160px] md:h-[160px] w-[80px] h-[80px] bg-[#FF5E4C] rounded-full flex items-center justify-center mx-auto ">
                <User className="text-white w-[40px] h-[40px] md:w-[80px] md:h-[80px]" />
              </div>
            </div>
            <div className="w-full text-center md:text-start">
              <h2 className="text-[24px] md:text-[32px] font-medium text-black mb-4 md:mb-[2px]">Welcome to Book for Paws</h2>
              <p className="text-[16px] md:text-[20px] text-[#8c8c8c] mb-2">Sign in to access your bookings, profile, and more</p>
              <p className="text-[14px] text-[#8c8c8c] mb-8">Use your mobile number — we&apos;ll text you a one-time code to sign in or create an account.</p>
              <button
                onClick={() => setShowLoginModal(true)}
                className="bg-[#FF5E4C] text-white px-8 py-4 rounded-[14px] font-medium text-[16px] md:text-[17px] hover:bg-[#FF4A36] transition-colors"
              >
                Sign In / Sign Up
              </button>
            </div>
          </div>
        </div>
  

        {/* Login Modal */ }
        < LoginSignupModal
    isOpen = { showLoginModal }
    onClose = {() => setShowLoginModal(false)}
onSuccess = { async() => {
  setShowLoginModal(false);
  // Refresh user data instead of reloading the page
  await refreshUserData();
}}
        />
      </>
    );
  }

return (
  <>
    <div className="min-h-screen bg-[#FAF7F2] md:px-[120px] md:py-[60px]">
      {/* Header */}
      <div className=" p-[16px] flex items-center ">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center justify-center w-8 h-8"
        >
          <ArrowLeft size={20} className="text-black" />
        </button>
        <h1 className="text-[20px] md:text-[24px] font-medium text-black tracking-[-1px]">Account</h1>
        <div className="w-8"></div>
      </div>

      {/* Main Content */}
      <div className="p-[20px] w-full flex flex-col md:flex-row  gap-[32px] md:gap-[120px]">
        {/* User Profile Section */}
        <div className="flex flex-col md:w-1/3 items-center p-[20px]">
          {/* Avatar */}
          <div className="w-[80px] h-[80px] md:w-[160px] md:h-[160px] bg-[#FF5E4C] rounded-full flex items-center justify-center mb-[14px]">
            <span className="text-white text-[40px] md:text-[80px] font-medium">
              {user?.name ? user.name.charAt(0).toUpperCase() : 'A'}
            </span>
          </div>

          {/* User Info */}
          <h2 className="text-[16px] md:text-[19px] font-medium text-black leading-[1.3]">
            Hi {user?.name || 'User'}, Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}
          </h2>
          <p className="text-[16px] md:text-[18px] text-[#9d9d9d] leading-[1.3]">{user?.mobile || '+91 9876543210'}</p>
          <p className="text-[16px] md:text-[18px] text-[#9d9d9d] leading-[1.3]">{user?.email || 'useraccount@gmail.com'}</p>
        </div>
        <div className='w-full'>
          {/* Main Navigation Cards */}
          <div className="space-y-[8px]">
            <div className="bg-white rounded-[16px] p-[24px] shadow-sm">
              <div className="flex items-center justify-between mb-[12px]">
                <span className="text-[20px] md:text-[22px] font-medium text-black leading-none tracking-[-1px]">Wishlist</span>
                <span className="text-[14px] text-[#8c8c8c]">{wishlistItems.length} saved</span>
              </div>
              {wishlistItems.length > 0 ? (
                <div className="space-y-[10px]">
                  {wishlistItems.slice(0, 5).map((item) => (
                    <button
                      key={`${item.type}-${item.id}`}
                      onClick={() => handleWishlistNavigate(item)}
                      className="w-full flex items-center justify-between gap-[10px] border border-[#EEE7DD] rounded-[12px] p-[10px] hover:bg-[#FAF7F2] transition-colors"
                    >
                      <div className="flex items-center gap-[10px] min-w-0">
                        <img
                          src={item.image || '/assets/BFPlogo.png'}
                          alt={item.title}
                          className="w-12 h-12 rounded-[8px] object-cover shrink-0"
                        />
                        <div className="text-left min-w-0">
                          <p className="text-[14px] text-black font-medium truncate">{item.title}</p>
                          <p className="text-[12px] text-[#8c8c8c] truncate">{item.subtitle || (item.type === 'event' ? 'Event' : 'Stay')}</p>
                        </div>
                      </div>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(event) => handleRemoveWishlistItem(item, event)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            handleRemoveWishlistItem(item, event);
                          }
                        }}
                        className="text-[12px] text-[#FF5E4C] px-[8px] py-[4px]"
                      >
                        Remove
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-[14px] text-[#8c8c8c]">No favourites yet. Save from property or event detail pages.</p>
              )}
            </div>

            {/* All Bookings Card */}
            <div
              onClick={handleViewAllBookings}
              className="bg-white rounded-[16px] p-[24px] shadow-sm cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-[14px]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <g clip-path="url(#clip0_3255_66)">
                      <path d="M14 3.5L23.3333 8.75V19.25L14 24.5L4.66667 19.25V8.75L14 3.5Z" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M14 14L23.3333 8.75" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M14 14V24.5" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M14 14L4.66667 8.75" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M18.6667 6.125L9.33333 11.375" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                    </g>
                    <defs>
                      <clipPath id="clip0_3255_66">
                        <rect width="28" height="28" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  <span className="text-[20px] md:text-[22px] font-medium text-black leading-none tracking-[-1px]">All Bookings</span>
                </div>

              </div>
            </div>

            {/* Help & Support Card */}
            <div
              onClick={() => navigate('/help')}
              className="bg-white rounded-[16px] p-[24px] shadow-sm cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-[14px]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <g clip-path="url(#clip0_3255_75)">
                      <path d="M4.66667 16.3333V12.8333C4.66667 10.358 5.65 7.98401 7.40034 6.23367C9.15068 4.48333 11.5246 3.5 14 3.5C16.4754 3.5 18.8493 4.48333 20.5997 6.23367C22.35 7.98401 23.3333 10.358 23.3333 12.8333V16.3333" stroke="#131313" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M21 22.1667C21 24.0998 17.8663 25.6667 14 25.6667" stroke="#131313" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M4.66667 16.3333C4.66667 15.7145 4.9125 15.121 5.35009 14.6834C5.78767 14.2458 6.38116 14 7 14H8.16667C8.78551 14 9.379 14.2458 9.81658 14.6834C10.2542 15.121 10.5 15.7145 10.5 16.3333V19.8333C10.5 20.4522 10.2542 21.0457 9.81658 21.4832C9.379 21.9208 8.78551 22.1667 8.16667 22.1667H7C6.38116 22.1667 5.78767 21.9208 5.35009 21.4832C4.9125 21.0457 4.66667 20.4522 4.66667 19.8333V16.3333Z" stroke="#131313" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M17.5 16.3333C17.5 15.7145 17.7458 15.121 18.1834 14.6834C18.621 14.2458 19.2145 14 19.8333 14H21C21.6188 14 22.2123 14.2458 22.6499 14.6834C23.0875 15.121 23.3333 15.7145 23.3333 16.3333V19.8333C23.3333 20.4522 23.0875 21.0457 22.6499 21.4832C22.2123 21.9208 21.6188 22.1667 21 22.1667H19.8333C19.2145 22.1667 18.621 21.9208 18.1834 21.4832C17.7458 21.0457 17.5 20.4522 17.5 19.8333V16.3333Z" stroke="#131313" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                    </g>
                    <defs>
                      <clipPath id="clip0_3255_75">
                        <rect width="28" height="28" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  <span className="text-[20px] md:text-[22px] font-medium text-black leading-none tracking-[-1px]">Help & Support</span>
                </div>

              </div>
            </div>
          </div>

          {/* Other Information Section */}
          <div className='pt-[20px]'>
            <h3 className="text-[16px] md:text-[18px] font-medium text-black leading-tight mb-[10px]">Other Information</h3>

            <div className="flex flex-col rounded-[14px] border-[1px] p-[20px] gap-[24px] border-[#DDD6CB]">
              {/* My Profile */}
              <div
                onClick={handleEditProfile}
                className="flex items-center cursor-pointer"
              >
                <div className="flex items-center space-x-[10px]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <g clip-path="url(#clip0_3255_86)">
                      <path d="M6 5.25C6 6.04565 6.31607 6.80871 6.87868 7.37132C7.44129 7.93393 8.20435 8.25 9 8.25C9.79565 8.25 10.5587 7.93393 11.1213 7.37132C11.6839 6.80871 12 6.04565 12 5.25C12 4.45435 11.6839 3.69129 11.1213 3.12868C10.5587 2.56607 9.79565 2.25 9 2.25C8.20435 2.25 7.44129 2.56607 6.87868 3.12868C6.31607 3.69129 6 4.45435 6 5.25Z" stroke="black" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M4.5 15.75V14.25C4.5 13.4544 4.81607 12.6913 5.37868 12.1287C5.94129 11.5661 6.70435 11.25 7.5 11.25H10.5C11.2956 11.25 12.0587 11.5661 12.6213 12.1287C13.1839 12.6913 13.5 13.4544 13.5 14.25V15.75" stroke="black" stroke-linecap="round" stroke-linejoin="round" />
                    </g>
                    <defs>
                      <clipPath id="clip0_3255_86">
                        <rect width="18" height="18" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  <span className="text-[14px] md:text-[16px] font-medium text-black tracking-[-0.7px]">My Profile</span>
                </div>

              </div>

              {ACCOUNT_VOUCHERS_ENABLED && (
                <div className="flex items-center justify-between cursor-pointer">
                  <div className="flex items-center space-x-[10px]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M2.25 6.75C2.25 6.55109 2.32902 6.36032 2.46967 6.21967C2.61032 6.07902 2.80109 6 3 6H15C15.1989 6 15.3897 6.07902 15.5303 6.21967C15.671 6.36032 15.75 6.55109 15.75 6.75V8.25C15.75 8.44891 15.671 8.63968 15.5303 8.78033C15.3897 8.92098 15.1989 9 15 9H3C2.80109 9 2.61032 8.92098 2.46967 8.78033C2.32902 8.63968 2.25 8.44891 2.25 8.25V6.75Z" stroke="#131313" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M9 6V15.75" stroke="#131313" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M14.2499 9V14.25C14.2499 14.6478 14.0918 15.0294 13.8105 15.3107C13.5292 15.592 13.1477 15.75 12.7499 15.75H5.24986C4.85203 15.75 4.4705 15.592 4.18919 15.3107C3.90789 15.0294 3.74986 14.6478 3.74986 14.25V9" stroke="#131313" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M5.62486 6.00002C5.12757 6.00002 4.65066 5.80248 4.29903 5.45085C3.9474 5.09922 3.74986 4.6223 3.74986 4.12502C3.74986 3.62774 3.9474 3.15083 4.29903 2.7992C4.65066 2.44757 5.12757 2.25002 5.62486 2.25002C6.34837 2.23742 7.05737 2.58847 7.65941 3.25739C8.26144 3.92632 8.72856 4.88208 8.99986 6.00002C9.27115 4.88208 9.73827 3.92632 10.3403 3.25739C10.9423 2.58847 11.6513 2.23742 12.3749 2.25002C12.8721 2.25002 13.349 2.44757 13.7007 2.7992C14.0523 3.15083 14.2499 3.62774 14.2499 4.12502C14.2499 4.6223 14.0523 5.09922 13.7007 5.45085C13.349 5.80248 12.8721 6.00002 12.3749 6.00002" stroke="#131313" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className="text-[14px] md:text-[16px] font-medium text-black tracking-[-0.7px]">Vouchers</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Log Out Button */}
          <div className=" text-center md:pt-[20px]">
            <button
              onClick={handleLogout}
              className="text-[#FF5E4C] text-[16px] md:text-[18px] font-medium hover:text-[#FF4A36] leading-none transition-colors"
            >
              Log out
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* All Bookings Modal */}
    {showAllBookings && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-[16px] w-full max-w-2xl max-h-[80vh] overflow-hidden">
          {/* Modal Header */}
          <div className="p-[20px] border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-[20px] md:text-[22px] font-semibold text-black">All Bookings ({pagination.total})</h2>
            <button
              onClick={handleCloseAllBookings}
              className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          {/* Modal Content */}
          <div className="max-h-[60vh] overflow-y-auto">
            {loadingBookings ? (
              <div className="p-[40px] text-center">
                <div className="w-8 h-8 border-2 border-[#FF5E4C] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                <p className="text-[16px] md:text-[17px] text-gray-500">Loading all bookings...</p>
              </div>
            ) : allBookings.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {allBookings.map((booking, index) => {
                  const stayInfo = getStayInfoFromBooking(booking);
                  return (
                  <div
                    key={booking.bookingId || index}
                    onClick={() => {
                      handleBookingClick(booking);
                      handleCloseAllBookings();
                    }}
                    className="p-[20px] hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="text-[16px] md:text-[17px] font-medium text-black truncate">
                          {stayInfo.title}
                        </h4>
                        <p className="text-[14px] md:text-[15px] text-gray-500 truncate">
                          {stayInfo.location}
                        </p>
                        <div className="flex items-center space-x-4 mt-2">
                          <p className="text-[12px] md:text-[13px] text-gray-500">
                            Check-in: {new Date(booking.checkIn).toLocaleDateString('en-US', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </p>
                          <p className="text-[12px] md:text-[13px] text-gray-500">
                            Check-out: {new Date(booking.checkOut).toLocaleDateString('en-US', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        {(() => {
                          const badge = getStayBookingBadge(booking);
                          return (
                            <div className={`px-3 py-1 rounded-full text-[12px] md:text-[13px] font-medium mb-1 ${badge.bgColor} ${badge.textColor}`}>
                              {badge.text}
                            </div>
                          );
                        })()}
                        <div className={`px-2 py-0.5 rounded-full text-[10px] md:text-[11px] font-medium mb-2 ${booking.paymentStatus === 'SUCCEEDED' ? 'bg-green-50 text-green-700' :
                          booking.paymentStatus === 'PENDING' ? 'bg-yellow-50 text-yellow-700' :
                            booking.paymentStatus === 'FAILED' ? 'bg-red-50 text-red-700' :
                              'bg-gray-50 text-gray-700'
                          }`}>
                          {booking.paymentStatus === 'SUCCEEDED' ? 'Paid' :
                            booking.paymentStatus === 'PENDING' ? 'Pending' :
                              booking.paymentStatus === 'FAILED' ? 'Failed' :
                                'N/A'}
                        </div>
                        {booking.pricingSummary?.grandTotal && (
                          <p className="text-[14px] md:text-[15px] font-semibold text-gray-900">
                            ₹{booking.pricingSummary.grandTotal.toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
                })}
              </div>
            ) : (
              <div className="p-[40px] text-center">
                <Box size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-[16px] md:text-[17px] text-gray-500">No bookings found</p>
                <p className="text-[14px] md:text-[15px] text-gray-400">Your bookings will appear here</p>
              </div>
            )}
          </div>
        </div>
      </div>
    )}

    {/* Edit Profile Modal */}
    {EditProfileModal}

  </>
);
};

export default Account;